import { lerp } from '../utils/math.js';
import { pointAtTime, sampleOrbit } from './orbit.js';

const TAP = '#526dff';
const HOLD = '#c7ff45';
const GOLD = '#ffd24a';
const DOUBLE = '#ff5bd6';
const SCRUB = '#45e6ff';

/** Pre-rendered glow sprites bucketed by hue: additive drawImage instead of per-item gradients. */
const glowSprites = new Map();

function getGlowSprite(hue, type) {
  const bucket = Math.round(hue / 10) * 10;
  const key = `${type}-${bucket}`;
  let sprite = glowSprites.get(key);
  if (sprite) return sprite;

  sprite = document.createElement('canvas');
  const sctx = sprite.getContext('2d');

  if (type === 'nebula') {
    // Three offset blobs make the cloud organic instead of a perfect disc.
    sprite.width = 256;
    sprite.height = 256;
    const blobs = [
      [128, 128, 120, 0.65],
      [92 + (bucket % 40), 108, 78, 0.52],
      [162, 156 - (bucket % 30), 70, 0.5]
    ];
    for (const [bx, by, br, alpha] of blobs) {
      const gradient = sctx.createRadialGradient(bx, by, 0, bx, by, br);
      gradient.addColorStop(0, `hsla(${bucket},100%,52%,${alpha * 0.77})`);
      gradient.addColorStop(0.55, `hsla(${bucket},100%,48%,${alpha * 0.3})`);
      gradient.addColorStop(1, `hsla(${bucket},100%,44%,0)`);
      sctx.fillStyle = gradient;
      sctx.fillRect(0, 0, 256, 256);
    }
  } else {
    sprite.width = 64;
    sprite.height = 64;
    const gradient = sctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    // Tinted core instead of white: additive stacking stays colored.
    gradient.addColorStop(0, `hsla(${bucket},95%,70%,.6)`);
    gradient.addColorStop(0.25, `hsla(${bucket},95%,62%,.45)`);
    gradient.addColorStop(1, `hsla(${bucket},95%,54%,0)`);
    sctx.fillStyle = gradient;
    sctx.fillRect(0, 0, 64, 64);
  }
  glowSprites.set(key, sprite);
  return sprite;
}

/**
 * Paints the static cosmos composition (nebulae, links, stars) in additive blending.
 * Shared by the renderer cache and gallery thumbnails.
 */
export function paintCosmosStatic(ctx, w, h, cosmos, { includeNebulae = true } = {}) {
  const scale = Math.min(w, h);
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  // Ambient dust: faint colored micro-stars, warming up with the era.
  const dustAlpha = 0.14 + (cosmos.eraIndex ?? 0) * 0.05;
  for (const mote of cosmos.dust || []) {
    ctx.fillStyle = `hsla(${mote.hue},70%,55%,${dustAlpha})`;
    ctx.beginPath();
    ctx.arc(mote.x * w, mote.y * h, Math.max(0.5, mote.size * (scale / 900)), 0, Math.PI * 2);
    ctx.fill();
  }

  if (includeNebulae) {
    for (const nebula of cosmos.nebulae || []) {
      const r = nebula.r * scale * 2;
      ctx.globalAlpha = Math.min(1, 0.9 * (nebula.bright ?? 1));
      ctx.drawImage(getGlowSprite(nebula.hue, 'nebula'), nebula.x * w - r, nebula.y * h - r, r * 2, r * 2);
      // Complementary inner core makes each cloud two-toned.
      const innerR = r * 0.45;
      ctx.globalAlpha = Math.min(1, 0.5 * (nebula.bright ?? 1));
      ctx.drawImage(
        getGlowSprite((nebula.hue + 45) % 360, 'nebula'),
        nebula.x * w - innerR, nebula.y * h - innerR, innerR * 2, innerR * 2
      );
    }
    ctx.globalAlpha = 1;
  }

  if ((cosmos.eraIndex ?? 0) >= 3 && cosmos.links?.length) {
    ctx.lineWidth = 1;
    for (const link of cosmos.links) {
      ctx.strokeStyle = `hsla(${link.a.hue},85%,75%,.14)`;
      ctx.beginPath();
      ctx.moveTo(link.a.x * w, link.a.y * h);
      ctx.lineTo(link.b.x * w, link.b.y * h);
      ctx.stroke();
    }
  }

  for (const star of cosmos.stars || []) {
    const x = star.x * w;
    const y = star.y * h;
    const bright = Math.min(2, star.bright ?? 1);
    const r = Math.max(0.7, star.size * (scale / 720) * bright);
    if (star.size >= 2 || star.core) {
      const haloR = r * (star.core ? 9 : 6);
      ctx.drawImage(getGlowSprite(star.hue, 'star'), x - haloR, y - haloR, haloR * 2, haloR * 2);
    }
    if (star.core) {
      ctx.strokeStyle = `hsla(${star.hue},95%,80%,.5)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - r * 7, y);
      ctx.lineTo(x + r * 7, y);
      ctx.moveTo(x, y - r * 7);
      ctx.lineTo(x, y + r * 7);
      ctx.stroke();
    }
    ctx.fillStyle = `hsl(${star.hue},90%,${star.core ? 78 : star.size >= 2 ? 66 : 60}%)`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** Renders a stored snapshot state to a square PNG data URL for the gallery. */
export function snapshotStateToDataUrl(state, size = 220) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#090a0d';
  ctx.fillRect(0, 0, size, size);
  if (state) paintCosmosStatic(ctx, size, size, state);
  return canvas.toDataURL('image/png');
}

/** One readable orbit, one playhead, chronological markers. */
export default class Renderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: false });
    this.w = 1;
    this.h = 1;
    this.beatPulse = 0;
    this.comboEnergy = 0;
    this.time = 0;
    this._cosmosCache = document.createElement('canvas');
    this._cosmosCacheKey = '';
    this._cosmosFieldSide = 1;
  }

  resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = this.canvas.clientWidth || window.innerWidth;
    this.h = this.canvas.clientHeight || window.innerHeight;
    this.canvas.width = Math.floor(this.w * dpr);
    this.canvas.height = Math.floor(this.h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  draw(state) {
    this.time = state.time ?? this.time + 0.016;
    this.beatPulse = lerp(this.beatPulse, state.beatPulse || 0, 0.32);
    this.comboEnergy = lerp(this.comboEnergy, Math.min(1, (state.combo || 0) / 50), 0.1);

    const { ctx } = this;
    ctx.save();
    const shake = (state.shake || 0) * 4;
    if (shake > 0.1) ctx.translate((Math.random() - 0.5) * shake, (Math.random() - 0.5) * shake);

    // Perfect-hit punch: a tiny canvas zoom, purely visual (audio clock untouched).
    const punch = state.punch || 0;
    if (punch > 0.001) {
      const zoom = 1 + punch * 0.015;
      ctx.translate(this.w / 2, this.h / 2);
      ctx.scale(zoom, zoom);
      ctx.translate(-this.w / 2, -this.h / 2);
    }

    this._drawBackground();

    if (state.mode === 'universe') {
      if (state.cosmos) this._drawCosmos(state.cosmos, { scrim: false });
      ctx.restore();
      return;
    }

    this._drawTopography();
    if (state.cosmos) this._drawCosmos(state.cosmos, { scrim: true, beatPulse: this.beatPulse });
    this._drawOrbit(state);
    this._drawCore(state);
    this._drawMarkers(state);
    this._drawPlayhead(state);

    for (const wave of state.waves || []) this._drawRipple(wave);
    for (const particle of state.particles || []) this._drawFragment(particle);

    if ((state.flash || 0) > 0) {
      ctx.fillStyle = state.overdrive
        ? `rgba(199,255,69,${state.flash * 0.08})`
        : `rgba(82,109,255,${state.flash * 0.06})`;
      ctx.fillRect(0, 0, this.w, this.h);
    }
    ctx.restore();
  }

  _x(value) { return value * this.w; }
  _y(value) { return value * this.h; }
  _scale() { return Math.min(this.w, this.h); }

  _drawBackground() {
    const { ctx, w, h } = this;
    ctx.fillStyle = '#090a0d';
    ctx.fillRect(-10, -10, w + 20, h + 20);
    const glow = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, this._scale() * 0.5);
    glow.addColorStop(0, `rgba(82,109,255,${0.028 + this.beatPulse * 0.02})`);
    glow.addColorStop(1, 'rgba(82,109,255,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, h);

    ctx.fillStyle = 'rgba(255,255,255,.022)';
    for (let y = 13; y < h; y += 31) {
      for (let x = 19; x < w; x += 31) {
        if ((x * 13 + y * 7) % 29 < 3) ctx.fillRect(x, y, 1, 1);
      }
    }
  }

  _drawTopography() {
    const { ctx, w, h } = this;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,.035)';
    ctx.lineWidth = 1;
    for (let row = 1; row <= 5; row++) {
      const baseY = (h / 6) * row;
      ctx.beginPath();
      for (let x = 0; x <= w; x += 24) {
        const y = baseY + Math.sin(x * 0.005 + row * 1.8 + this.time * 0.05) * 8;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * The cache is an oversized square (side = screen diagonal + halo margin),
   * centered on the screen: whatever the rotation angle, the screen stays
   * fully covered and no bitmap edge (cut stars, sliced halos) is visible.
   */
  _ensureCosmosCache(cosmos) {
    const side = Math.ceil(Math.hypot(this.w, this.h) * 1.25);
    const key = `${cosmos.version}|${side}`;
    if (key === this._cosmosCacheKey) return;
    this._cosmosCacheKey = key;
    this._cosmosFieldSide = side;
    const cache = this._cosmosCache;
    // Cap the bitmap size: the field is soft glows, upscaling is invisible.
    const dpr = Math.min(this.canvas.width / this.w, 2600 / side);
    cache.width = Math.ceil(side * dpr);
    cache.height = Math.ceil(side * dpr);
    const cctx = cache.getContext('2d');
    // Screen-space coordinates, shifted so the screen sits at the cache center.
    cctx.setTransform(dpr, 0, 0, dpr, dpr * (side - this.w) / 2, dpr * (side - this.h) / 2);
    cctx.clearRect(-(side - this.w) / 2, -(side - this.h) / 2, side, side);
    // Nebulae are excluded from the cache: they breathe and are drawn live.
    paintCosmosStatic(cctx, this.w, this.h, cosmos, { includeNebulae: false });
  }

  _drawCosmos(cosmos, { scrim = true, beatPulse = 0 } = {}) {
    const { ctx, w, h } = this;
    this._ensureCosmosCache(cosmos);
    const hues = cosmos.palette.hues || [210, 250];
    const shift = cosmos.palette.hueShift || 0;

    // Aurora backdrop: three color fields with slowly rotating hues, stronger with each era.
    const glow = cosmos.overdriveGlow || 0;
    const auroraStrength = (cosmos.eraIndex + 1) * 0.038 * (1 + glow * 0.6);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const sweep = Math.sin(this.time * 0.11) * 0.22;
    const hueDrift = this.time * 4;
    const auroras = [
      [w * (0.22 + sweep), h * 0.18, (hues[0] + shift + hueDrift) % 360],
      [w * (0.8 - sweep), h * 0.85, (hues[2] + shift + hueDrift * 1.3) % 360],
      [w * (0.5 + sweep * 0.6), h * (0.5 + Math.cos(this.time * 0.09) * 0.3), (hues[1] + shift - hueDrift) % 360]
    ];
    for (const [ax, ay, hue] of auroras) {
      const gradient = ctx.createRadialGradient(ax, ay, 0, ax, ay, this._scale() * 0.85);
      gradient.addColorStop(0, `hsla(${(hue + 360) % 360},95%,48%,${auroraStrength + beatPulse * 0.022})`);
      gradient.addColorStop(1, `hsla(${(hue + 360) % 360},95%,48%,0)`);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);
    }
    ctx.restore();

    ctx.save();
    if (cosmos.rotation) {
      // Zoom just enough for the rotated field to keep covering the screen
      // corners (no straight bitmap edge or star cut can enter the view).
      const cos = Math.abs(Math.cos(cosmos.rotation));
      const sin = Math.abs(Math.sin(cosmos.rotation));
      const cover = Math.max((w * cos + h * sin) / w, (w * sin + h * cos) / h) * 1.02;
      ctx.translate(w / 2, h / 2);
      ctx.rotate(cosmos.rotation);
      ctx.scale(cover, cover);
      ctx.translate(-w / 2, -h / 2);
    }

    // Lit nebulae breathe individually: drawn live, out of the static cache.
    ctx.globalCompositeOperation = 'lighter';
    for (const nebula of cosmos.nebulae || []) {
      const phase = nebula.phase ?? 0;
      const pulse = 1 + Math.sin(this.time * 0.55 + phase) * 0.07;
      const breath = 0.86 + Math.sin(this.time * 0.8 + phase * 1.7) * 0.14;
      const r = nebula.r * this._scale() * 2 * pulse;
      ctx.globalAlpha = Math.min(1, 0.9 * (nebula.bright ?? 1) * breath * (1 + glow * 0.35));
      ctx.drawImage(getGlowSprite(nebula.hue, 'nebula'), nebula.x * w - r, nebula.y * h - r, r * 2, r * 2);
      const innerR = r * 0.45;
      ctx.globalAlpha = Math.min(1, 0.5 * (nebula.bright ?? 1) * breath);
      ctx.drawImage(
        getGlowSprite((nebula.hue + 45) % 360, 'nebula'),
        nebula.x * w - innerR, nebula.y * h - innerR, innerR * 2, innerR * 2
      );
    }
    ctx.globalCompositeOperation = 'source-over';

    // The cached field is composited slightly brighter on the beat.
    // The cache is an oversized centered square (see _ensureCosmosCache).
    const side = this._cosmosFieldSide;
    const cacheX = -(side - w) / 2;
    const cacheY = -(side - h) / 2;
    ctx.globalAlpha = Math.min(1, 0.92 + beatPulse * 0.08);
    ctx.drawImage(this._cosmosCache, cacheX, cacheY, side, side);
    ctx.globalAlpha = 1;

    ctx.globalCompositeOperation = 'lighter';

    // Incandescent Overdrive: the whole field is composited again additively (global bloom).
    if (glow > 0.01) {
      ctx.globalAlpha = glow * (0.42 + beatPulse * 0.2);
      ctx.drawImage(this._cosmosCache, cacheX, cacheY, side, side);
      ctx.globalAlpha = 1;
    }

    // Ignition flashes: expanding cross rays + halo (0.45 s).
    for (const flash of cosmos.flashes || []) {
      const x = flash.x * w;
      const y = flash.y * h;
      const grow = 1 - flash.life;
      const rayLength = this._scale() * (flash.big ? 0.075 : 0.04) * (0.4 + grow);
      ctx.strokeStyle = `hsla(${flash.hue},95%,80%,${flash.life * 0.9})`;
      ctx.lineWidth = flash.big ? 2 : 1.25;
      ctx.beginPath();
      for (const angle of [0, Math.PI / 2, Math.PI / 4, -Math.PI / 4]) {
        ctx.moveTo(x - Math.cos(angle) * rayLength, y - Math.sin(angle) * rayLength);
        ctx.lineTo(x + Math.cos(angle) * rayLength, y + Math.sin(angle) * rayLength);
      }
      ctx.stroke();
      const haloR = rayLength * 0.8;
      ctx.globalAlpha = flash.life * 0.8;
      ctx.drawImage(getGlowSprite(flash.hue, 'star'), x - haloR, y - haloR, haloR * 2, haloR * 2);
      ctx.globalAlpha = 1;
    }

    // Live twinkle across a wide range of stars.
    let drawn = 0;
    for (const star of cosmos.stars || []) {
      if (star.size < 1.2 && !star.core) continue;
      if (++drawn > 80) break;
      const alpha = 0.2 + 0.2 * Math.sin(this.time * 2.4 + star.tw);
      if (alpha <= 0.03) continue;
      ctx.fillStyle = `hsla(${star.hue},95%,75%,${alpha})`;
      ctx.beginPath();
      ctx.arc(star.x * w, star.y * h, Math.max(1, star.size * (this._scale() / 600)), 0, Math.PI * 2);
      ctx.fill();
    }

    // Comets: bright head + additive gradient trail (ambient shooting stars are smaller).
    for (const comet of cosmos.comets || []) {
      const t = Math.min(1, comet.t);
      const scale = comet.small ? 0.55 : 1;
      const headX = (comet.x0 + (comet.x1 - comet.x0) * t) * w;
      const headY = (comet.y0 + (comet.y1 - comet.y0) * t) * h;
      const steps = comet.small ? 8 : 12;
      for (let i = 1; i <= steps; i++) {
        const tt = Math.max(0, t - i * 0.018);
        const x = (comet.x0 + (comet.x1 - comet.x0) * tt) * w;
        const y = (comet.y0 + (comet.y1 - comet.y0) * tt) * h;
        const alpha = (1 - i / (steps + 1)) * (comet.small ? 0.35 : 0.5);
        ctx.fillStyle = `hsla(${comet.hue},100%,${78 - i * 2}%,${alpha})`;
        ctx.beginPath();
        ctx.arc(x, y, Math.max(1, (5 - i * 0.35) * scale), 0, Math.PI * 2);
        ctx.fill();
      }
      const haloR = this._scale() * 0.03 * scale;
      ctx.drawImage(getGlowSprite(comet.hue, 'star'), headX - haloR, headY - haloR, haloR * 2, haloR * 2);
      ctx.fillStyle = `rgba(255,255,255,${comet.small ? 0.7 : 0.95})`;
      ctx.beginPath();
      ctx.arc(headX, headY, 3 * scale, 0, Math.PI * 2);
      ctx.fill();
    }

    // Combo bursts: full-screen colored rays from the galactic core.
    for (const burst of cosmos.bursts || []) {
      const life = Math.max(0, burst.life);
      const reach = this._scale() * (1.4 - life * 0.5) * (burst.mini ? 0.35 : 1);
      const bx = burst.x * w;
      const by = burst.y * h;
      ctx.lineCap = 'round';
      for (let i = 0; i < burst.rays; i++) {
        const angle = burst.seed + (Math.PI * 2 * i) / burst.rays;
        const rayHue = (burst.hue + i * 14) % 360;
        ctx.strokeStyle = `hsla(${rayHue},100%,70%,${life * 0.5})`;
        ctx.lineWidth = 1.5 + life * 2.5;
        ctx.beginPath();
        ctx.moveTo(bx + Math.cos(angle) * reach * 0.06, by + Math.sin(angle) * reach * 0.06);
        ctx.lineTo(bx + Math.cos(angle) * reach, by + Math.sin(angle) * reach);
        ctx.stroke();
      }
    }

    // Supernova shards flying outward.
    for (const spark of cosmos.sparks || []) {
      ctx.fillStyle = `hsla(${spark.hue},100%,75%,${Math.max(0, spark.life)})`;
      ctx.beginPath();
      ctx.arc(spark.x * w, spark.y * h, 1.5 + spark.life * 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';

    for (const hole of cosmos.blackHoles || []) {
      const x = hole.x * w;
      const y = hole.y * h;
      const fade = Math.min(1, hole.life / hole.maxLife + 0.25);
      const r = hole.radius * this._scale() * 0.4;
      const void_ = ctx.createRadialGradient(x, y, 0, x, y, r);
      void_.addColorStop(0, `rgba(0,0,0,${0.94 * fade})`);
      void_.addColorStop(0.7, `rgba(0,0,0,${0.6 * fade})`);
      void_.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = void_;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = `rgba(255,91,100,${0.45 * fade})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(x, y, r * 0.45 + Math.sin(this.time * 5) * 2, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Supernovae: triple shockwave + saturated flash. Golden mini-novas stay local.
    ctx.globalCompositeOperation = 'lighter';
    for (const wave of cosmos.shockwaves || []) {
      const hue = wave.hue ?? (hues[3] + shift) % 360;
      const life = Math.max(0, wave.life);
      const cx = (wave.x ?? 0.5) * w;
      const cy = (wave.y ?? 0.5) * h;
      const radius = wave.r * this._scale() * (wave.mini ? 0.7 : 2.2);
      ctx.strokeStyle = `hsla(${hue},100%,72%,${life * 0.9})`;
      ctx.lineWidth = wave.mini ? 3 : 5;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = `hsla(${(hue + 40) % 360},100%,85%,${life * 0.45})`;
      ctx.lineWidth = wave.mini ? 7 : 16;
      ctx.beginPath();
      ctx.arc(cx, cy, radius * 0.88, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = `hsla(${(hue + 80) % 360},100%,78%,${life * 0.3})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, radius * 1.16, 0, Math.PI * 2);
      ctx.stroke();
      if (!wave.mini) {
        ctx.fillStyle = `hsla(${hue},100%,65%,${life * 0.11})`;
        ctx.fillRect(0, 0, w, h);
      }
    }
    ctx.restore();

    // Readability scrim: darken the playfield ring so gameplay always dominates.
    if (scrim) {
      const scrimGradient = ctx.createRadialGradient(
        w / 2, h / 2, 0,
        w / 2, h / 2, this._scale() * 0.46
      );
      scrimGradient.addColorStop(0, 'rgba(9,10,13,.58)');
      scrimGradient.addColorStop(0.82, 'rgba(9,10,13,.42)');
      scrimGradient.addColorStop(1, 'rgba(9,10,13,0)');
      ctx.fillStyle = scrimGradient;
      ctx.fillRect(0, 0, w, h);
    }
  }

  _drawOrbit(state) {
    const { ctx } = this;
    const points = sampleOrbit(state.time, state.bpm, state.direction, 120);
    ctx.save();
    ctx.strokeStyle = state.overdrive ? 'rgba(199,255,69,.48)' : 'rgba(255,255,255,.17)';
    ctx.lineWidth = state.overdrive ? 2 : 1.25;
    ctx.beginPath();
    points.forEach((point, index) => {
      const x = this._x(point.x);
      const y = this._y(point.y);
      if (index === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Beat ticks make speed and direction readable.
    for (let i = 0; i < 8; i++) {
      const beatPoint = pointAtTime(state.time + i * (60 / state.bpm), state.bpm, state.direction);
      const x = this._x(beatPoint.x);
      const y = this._y(beatPoint.y);
      ctx.fillStyle = i === 0 ? 'rgba(255,255,255,.5)' : 'rgba(255,255,255,.16)';
      ctx.beginPath();
      ctx.arc(x, y, i === 0 ? 2.5 : 1.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  _drawCore(state) {
    const { ctx, w, h } = this;
    const cx = w / 2;
    const cy = h / 2;
    const base = this._scale() * 0.058;
    const segments = 8 + (state.comboTier || 0) * 4;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(this.time * (state.overdrive ? 0.12 : 0.035));
    for (let i = 0; i < segments; i++) {
      const start = (Math.PI * 2 * i) / segments;
      const end = start + (Math.PI * 2 / segments) * 0.58;
      ctx.strokeStyle = state.overdrive && i % 2 === 0 ? HOLD : TAP;
      ctx.globalAlpha = 0.34 + this.comboEnergy * 0.42;
      ctx.lineWidth = state.overdrive ? 2.5 : 1.5;
      ctx.beginPath();
      ctx.arc(0, 0, base + this.beatPulse * 3, start, end);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.fillStyle = state.overdrive ? HOLD : '#f3f4f6';
    ctx.beginPath();
    ctx.arc(0, 0, 2.5 + this.beatPulse, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  _drawMarkers(state) {
    const { ctx } = this;
    const notes = state.visibleNotes || [];
    for (let index = notes.length - 1; index >= 0; index--) {
      const view = notes[index];
      const note = view.note;
      if (note.type === 'hold') {
        this._drawHoldArc(state, view, index);
        continue;
      }
      if (note.type === 'scrub') {
        this._drawScrubZigzag(state, view, index);
        continue;
      }

      const point = pointAtTime(note.t, state.bpm, state.direction);
      const x = this._x(point.x);
      const y = this._y(point.y);
      const next = view.isNext;
      const gold = note.gold;
      const isDouble = note.type === 'double';
      const size = this._scale() * (next ? 0.014 : 0.009) * (gold ? 1.35 : 1);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(point.angle);
      ctx.fillStyle = gold ? GOLD : isDouble ? DOUBLE : next ? '#f3f4f6' : TAP;
      ctx.globalAlpha = next ? 1 : Math.max(0.22, 0.62 - index * 0.07 + (gold || isDouble ? 0.2 : 0));
      if (isDouble) {
        // Two stacked bars: read instantly as "tap twice".
        const half = note.state === 'half';
        ctx.beginPath();
        ctx.roundRect(-size, -size * 0.95, size * 2, size * 0.62, size * 0.31);
        ctx.fill();
        if (half) ctx.globalAlpha *= 0.35; // first tap done: top bar fades
        ctx.beginPath();
        ctx.roundRect(-size, size * 0.33, size * 2, size * 0.62, size * 0.31);
        ctx.fill();
        ctx.globalAlpha = next ? 1 : 0.5;
        ctx.strokeStyle = `rgba(255,91,214,${next ? 0.85 : 0.4})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(0, 0, size * (1.8 + this.beatPulse * 0.3), 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.roundRect(-size, -size * 0.45, size * 2, size * 0.9, size * 0.45);
        ctx.fill();
        if (gold) {
          ctx.strokeStyle = `rgba(255,210,74,${next ? 0.8 : 0.45})`;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(0, 0, size * (1.7 + this.beatPulse * 0.3), 0, Math.PI * 2);
          ctx.stroke();
        } else if (next) {
          ctx.strokeStyle = 'rgba(255,255,255,.35)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(0, 0, size * (1.5 + this.beatPulse * 0.2), 0, Math.PI * 2);
          ctx.stroke();
        }
      }
      ctx.restore();
    }
  }

  /** Zigzag line along the orbit segment: rub it while holding. */
  _drawScrubZigzag(state, view, index) {
    const { ctx } = this;
    const note = view.note;
    const scrubbing = note.state === 'scrubbing';
    const samples = Math.max(16, Math.ceil(note.duration * state.bpm / 8));
    const amplitude = this._scale() * 0.012;
    // Agitation feeds the glow: a well-rubbed zigzag burns bright.
    const charge = note.agitationTarget > 0 ? Math.min(1, note.agitation / note.agitationTarget) : 0;

    ctx.save();
    ctx.strokeStyle = SCRUB;
    ctx.globalAlpha = view.isNext || scrubbing
      ? 0.55 + charge * 0.45
      : Math.max(0.22, 0.55 - index * 0.06);
    ctx.lineWidth = scrubbing ? 3 + charge * 2.5 : 2.5;
    ctx.lineJoin = 'miter';
    ctx.beginPath();
    for (let i = 0; i <= samples; i++) {
      const time = note.t + (note.duration * i) / samples;
      const point = pointAtTime(time, state.bpm, state.direction);
      const side = i === 0 || i === samples ? 0 : (i % 2 === 0 ? 1 : -1);
      const x = this._x(point.x) + Math.cos(point.angle + Math.PI / 2) * amplitude * side;
      const y = this._y(point.y) + Math.sin(point.angle + Math.PI / 2) * amplitude * side;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    const head = pointAtTime(note.t, state.bpm, state.direction);
    ctx.fillStyle = scrubbing ? '#f3f4f6' : SCRUB;
    ctx.globalAlpha = view.isNext || scrubbing ? 1 : 0.7;
    ctx.beginPath();
    ctx.arc(this._x(head.x), this._y(head.y), this._scale() * 0.012, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  _drawHoldArc(state, view, index) {
    const { ctx } = this;
    const note = view.note;
    const samples = Math.max(10, Math.ceil(note.duration * state.bpm / 15));
    ctx.save();
    ctx.strokeStyle = HOLD;
    ctx.globalAlpha = view.isNext || note.state === 'holding' ? 0.9 : Math.max(0.22, 0.58 - index * 0.06);
    ctx.lineWidth = note.state === 'holding' ? 5 : 3;
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (let i = 0; i <= samples; i++) {
      const time = note.t + (note.duration * i) / samples;
      const point = pointAtTime(time, state.bpm, state.direction);
      const x = this._x(point.x);
      const y = this._y(point.y);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    const head = pointAtTime(note.t, state.bpm, state.direction);
    ctx.fillStyle = note.state === 'holding' ? '#f3f4f6' : HOLD;
    ctx.beginPath();
    ctx.arc(this._x(head.x), this._y(head.y), this._scale() * 0.012, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  _drawPlayhead(state) {
    const { ctx } = this;
    const point = state.playhead;
    if (!point) return;

    // Short directional trail, doubled while FEVER is running.
    ctx.save();
    ctx.lineCap = 'round';
    const trailCount = state.fever ? 10 : 5;
    for (let i = trailCount; i >= 1; i--) {
      const trail = pointAtTime(state.time - i * 0.035, state.bpm, state.direction);
      const strength = ((trailCount + 1 - i) / (trailCount + 1)) * 0.36;
      ctx.fillStyle = state.fever && !state.overdrive
        ? `rgba(255,210,74,${strength})`
        : state.overdrive
          ? `rgba(199,255,69,${strength})`
          : `rgba(82,109,255,${strength})`;
      ctx.beginPath();
      ctx.arc(this._x(trail.x), this._y(trail.y), 2 + (trailCount + 1 - i) * (state.fever ? 0.25 : 0.4), 0, Math.PI * 2);
      ctx.fill();
    }

    const x = this._x(point.x);
    const y = this._y(point.y);
    const radius = this._scale() * (0.014 + this.beatPulse * 0.002);
    ctx.fillStyle = state.overdrive ? HOLD : '#f3f4f6';
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = state.overdrive ? 'rgba(199,255,69,.5)' : 'rgba(82,109,255,.7)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, radius * 1.6, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  _drawRipple(wave) {
    const { ctx } = this;
    const radius = wave.r * this._scale();
    ctx.save();
    ctx.strokeStyle = `hsla(${wave.hue},78%,67%,${Math.max(0, wave.life) * 0.65})`;
    ctx.lineWidth = wave.amplify ? 2 : 1.25;
    ctx.beginPath();
    ctx.arc(this._x(wave.x), this._y(wave.y), radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  _drawFragment(particle) {
    const { ctx } = this;
    const alpha = particle.life / particle.maxLife;
    const size = Math.max(1.5, particle.size * this._scale() * 0.6);
    ctx.save();
    ctx.translate(this._x(particle.x), this._y(particle.y));
    ctx.rotate((particle.vx + particle.vy) * 8);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = `hsl(${particle.hue},72%,66%)`;
    ctx.fillRect(-size, -size * 0.3, size * 2, size * 0.6);
    ctx.restore();
  }
}
